import { useState, useEffect, useMemo } from 'react';
import type { GanttTask } from '../types/gantt';
import type { Gardener } from '../data/gardeners';
import type { Machine } from '../data/machines';
import type { Project } from '../data/projects';
import { getMachines, getMachinesByOrganization } from '../data/machines';
import { getProjects, getProjectDisplayName } from '../data/projects';

type TaskEditModalProps = {
	task: GanttTask | null;
	gardeners: Gardener[];
	organizationId?: string;
	onSave: (taskId: string, updates: Partial<GanttTask>) => void;
	onClose: () => void;
};

export default function TaskEditModal({
	task,
	gardeners,
	organizationId,
	onSave,
	onClose
}: TaskEditModalProps) {
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');
	const [selectedGardeners, setSelectedGardeners] = useState<string[]>([]);
	const [selectedMachines, setSelectedMachines] = useState<string[]>([]);
	const [status, setStatus] = useState<GanttTask['status']>('not_started');
	const [conflicts, setConflicts] = useState<Array<{ resource: string; project: string; dates: string }>>([]);
	
	const allMachines = useMemo(() => {
		if (organizationId) {
			return getMachinesByOrganization(organizationId);
		}
		return getMachines();
	}, [organizationId]);
	
	const [projects, setProjects] = useState<Project[]>([]);
	
	useEffect(() => {
		const loadProjects = async () => {
			try {
				const loadedProjects = await getProjects();
				setProjects(loadedProjects);
			} catch (err) {
				console.error('Failed to load projects:', err);
				setProjects([]);
			}
		};
		loadProjects();
	}, []);
	
	// Initialize form when task changes
	useEffect(() => {
		if (task) {
			setStartDate(task.startDate.split('T')[0]);
			setEndDate(task.endDate.split('T')[0]);
			setSelectedGardeners(task.gardenerIds || (task.gardenerId ? [task.gardenerId] : []));
			setSelectedMachines(task.machineIds || []);
			setStatus(task.status || 'not_started');
		}
	}, [task]);
	
	// Check for conflicts when dates or resources change
	useEffect(() => {
		if (!task || !startDate || !endDate) {
			setConflicts([]);
			return;
		}
		
		const conflictsList: Array<{ resource: string; project: string; dates: string }> = [];
		const start = new Date(startDate);
		const end = new Date(endDate);
		
		// Check gardener conflicts
		selectedGardeners.forEach(gardenerId => {
			projects.forEach(project => {
				if (project.ganttData && project.id !== task.projectId) {
					project.ganttData.forEach(otherTask => {
						const taskGardenerIds = otherTask.gardenerIds || (otherTask.gardenerId ? [otherTask.gardenerId] : []);
						if (taskGardenerIds.includes(gardenerId)) {
							const otherStart = new Date(otherTask.startDate);
							const otherEnd = new Date(otherTask.endDate);
							
							// Check if dates overlap
							if ((start <= otherEnd && end >= otherStart)) {
								const gardener = gardeners.find(g => g.id === gardenerId);
								conflictsList.push({
									resource: gardener?.name || gardenerId,
					project: getProjectDisplayName(project),
									dates: `${otherTask.startDate.split('T')[0]} to ${otherTask.endDate.split('T')[0]}`
								});
							}
						}
					});
				}
			});
		});
		
		// Check machine conflicts
		selectedMachines.forEach(machineId => {
			projects.forEach(project => {
				if (project.ganttData && project.id !== task.projectId) {
					project.ganttData.forEach(otherTask => {
						if (otherTask.machineIds?.includes(machineId)) {
							const otherStart = new Date(otherTask.startDate);
							const otherEnd = new Date(otherTask.endDate);
							
							// Check if dates overlap
							if ((start <= otherEnd && end >= otherStart)) {
								const machine = allMachines.find(m => m.id === machineId);
								conflictsList.push({
									resource: machine?.name || machineId,
					project: getProjectDisplayName(project),
									dates: `${otherTask.startDate.split('T')[0]} to ${otherTask.endDate.split('T')[0]}`
								});
							}
						}
					});
				}
			});
		});
		
		setConflicts(conflictsList);
	}, [task, startDate, endDate, selectedGardeners, selectedMachines, projects, gardeners, allMachines]);
	
	if (!task) return null;
	
	const handleSave = () => {
		if (!startDate || !endDate) {
			alert('Please select both start and end dates');
			return;
		}
		
		const updates: Partial<GanttTask> = {
			startDate: new Date(startDate).toISOString(),
			endDate: new Date(endDate).toISOString(),
			gardenerIds: selectedGardeners,
			// Always send array so parent can persist removals (DB sync treats undefined as "unchanged")
			machineIds: selectedMachines,
			status
		};
		
		// Calculate duration
		const start = new Date(startDate);
		const end = new Date(endDate);
		const duration = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
		updates.duration = duration;
		
		onSave(task.id, updates);
		onClose();
	};
	
	const toggleGardener = (gardenerId: string) => {
		setSelectedGardeners(prev =>
			prev.includes(gardenerId)
				? prev.filter(id => id !== gardenerId)
				: [...prev, gardenerId]
		);
	};
	
	const toggleMachine = (machineId: string) => {
		setSelectedMachines(prev =>
			prev.includes(machineId)
				? prev.filter(id => id !== machineId)
				: [...prev, machineId]
		);
	};
	
	return (
		<div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
			<div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
				<div className="modal-content">
					<div className="modal-header">
						<h5 className="modal-title">Edit Task: {task.name}</h5>
						<button type="button" className="btn-close" onClick={onClose}></button>
					</div>
					<div className="modal-body">
						{/* Dates */}
						<div className="row g-3 mb-3">
							<div className="col-md-6">
								<label className="form-label">Start Date</label>
								<input
									type="date"
									className="form-control"
									value={startDate}
									onChange={(e) => setStartDate(e.target.value)}
								/>
							</div>
							<div className="col-md-6">
								<label className="form-label">End Date</label>
								<input
									type="date"
									className="form-control"
									value={endDate}
									onChange={(e) => setEndDate(e.target.value)}
								/>
							</div>
						</div>
						
						{/* Status */}
						<div className="mb-3">
							<label className="form-label">Status</label>
							<select
								className="form-select"
								value={status}
								onChange={(e) => setStatus(e.target.value as GanttTask['status'])}
							>
								<option value="not_started">Not Started</option>
								<option value="in_progress">In Progress</option>
								<option value="completed">Completed</option>
								<option value="at_risk">At Risk</option>
								<option value="delayed">Delayed</option>
								<option value="blocked">Blocked</option>
							</select>
						</div>
						
						{/* Gardeners */}
						<div className="mb-3">
							<label className="form-label">Assign Gardeners</label>
							<div className="border rounded p-2" style={{ maxHeight: '150px', overflowY: 'auto' }}>
								{gardeners.map(gardener => (
									<div key={gardener.id} className="form-check">
										<input
											className="form-check-input"
											type="checkbox"
											id={`gardener-${gardener.id}`}
											checked={selectedGardeners.includes(gardener.id)}
											onChange={() => toggleGardener(gardener.id)}
										/>
										<label className="form-check-label" htmlFor={`gardener-${gardener.id}`}>
											{gardener.name} {gardener.role && `(${gardener.role})`}
										</label>
									</div>
								))}
							</div>
						</div>
						
						{/* Machines */}
						<div className="mb-3">
							<label className="form-label">Assign Machines</label>
							<div className="border rounded p-2" style={{ maxHeight: '150px', overflowY: 'auto' }}>
								{allMachines.map(machine => (
									<div key={machine.id} className="form-check">
										<input
											className="form-check-input"
											type="checkbox"
											id={`machine-${machine.id}`}
											checked={selectedMachines.includes(machine.id)}
											onChange={() => toggleMachine(machine.id)}
											disabled={machine.state === 'maintenance' || machine.state === 'unavailable'}
										/>
										<label className="form-check-label" htmlFor={`machine-${machine.id}`}>
											{machine.name} 
											{machine.state !== 'available' && (
												<span className="badge text-bg-warning ms-2">
													{machine.state === 'in_use' ? 'In Use' :
													 machine.state === 'maintenance' ? 'Maintenance' :
													 'Unavailable'}
												</span>
											)}
										</label>
									</div>
								))}
							</div>
						</div>
						
						{/* Conflicts */}
						{conflicts.length > 0 && (
							<div className="alert alert-warning">
								<strong>⚠️ Resource Conflicts Detected:</strong>
								<ul className="mb-0 mt-2">
									{conflicts.map((conflict, idx) => (
										<li key={idx}>
											<strong>{conflict.resource}</strong> is already assigned to <strong>{conflict.project}</strong> from {conflict.dates}
										</li>
									))}
								</ul>
							</div>
						)}
					</div>
					<div className="modal-footer">
						<button type="button" className="btn btn-secondary" onClick={onClose}>
							Cancel
						</button>
						<button type="button" className="btn btn-success" onClick={handleSave}>
							Save Changes
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}

